import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FaCamera,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaTimes,
  FaTruck,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { authService } from '../../../../services/apiService';
import { yardVehicleService } from '../../../../services/yardVehicleService';
import {
  DAMAGE_TYPE_OPTIONS,
  FUEL_TYPE_OPTIONS,
  SOURCE_TYPE_OPTIONS,
  TRANSMISSION_OPTIONS,
  formatIndianRegistration,
  isValidIndianRegistration,
} from '../../../../utils/indianRegistration';

const STEPS = [
  { id: 1, label: 'Vehicle photo' },
  { id: 2, label: 'Vehicle details' },
  { id: 3, label: 'Registration & Vahan' },
];

const emptyForm = () => ({
  photoUrl: '',
  make: '',
  model: '',
  year: '',
  fuelType: '',
  transmission: '',
  damageType: [],
  sourceType: '',
  registrationNumber: '',
});

function StepIndicator({ step }) {
  return (
    <ol className="flex items-center gap-2 mb-6">
      {STEPS.map((s, idx) => {
        const done = step > s.id;
        const active = step === s.id;
        return (
          <li key={s.id} className="flex items-center gap-2 flex-1 min-w-0">
            <span
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                done
                  ? 'bg-emerald-500 text-white'
                  : active
                    ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500'
                    : 'bg-slate-100 text-slate-400'
              }`}
            >
              {done ? <FaCheckCircle /> : s.id}
            </span>
            <span
              className={`hidden sm:block text-xs font-medium truncate ${
                active ? 'text-emerald-700' : 'text-slate-500'
              }`}
            >
              {s.label}
            </span>
            {idx < STEPS.length - 1 && (
              <span className="hidden sm:block h-px flex-1 bg-slate-200 mx-1" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function VahanFeedback({ state, result, mismatchAttempts }) {
  if (state === 'idle') return null;
  if (state === 'loading') {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
        <FaSpinner className="animate-spin text-emerald-600" />
        Checking Vahan RC record…
      </div>
    );
  }
  if (!result) return null;

  if (result.status === 'match') {
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-start gap-2">
        <FaCheckCircle className="mt-0.5 shrink-0" />
        <span>{result.message}</span>
      </div>
    );
  }

  if (result.status === 'api_down') {
    return (
      <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-start gap-2">
        <FaExclamationTriangle className="mt-0.5 shrink-0" />
        <span>
          Vahan unavailable — your declaration will be accepted with{' '}
          <strong>spot check required</strong>.
        </span>
      </div>
    );
  }

  if (result.status === 'mismatch' && result.rc) {
    return (
      <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
        <p className="font-semibold flex items-center gap-2">
          <FaExclamationTriangle />
          RC mismatch — correct your entry to match Vahan
        </p>
        <p className="mt-1 text-xs text-red-800">
          RC record: {result.rc.make} {result.rc.model}
          {result.rc.year ? ` (${result.rc.year})` : ''}
          {result.rc.fuelType ? ` · ${result.rc.fuelType}` : ''}
        </p>
        {mismatchAttempts >= 1 && (
          <p className="mt-2 text-xs text-red-700">
            If details are correct but still mismatch, submit again to flag for admin review.
          </p>
        )}
      </div>
    );
  }

  return null;
}

export default function YardVehicleEntryForm({ onSuccess, onCancel }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vahanState, setVahanState] = useState('idle');
  const [vahanResult, setVahanResult] = useState(null);
  const [mismatchAttempts, setMismatchAttempts] = useState(0);
  const vahanTimer = useRef(null);
  const autoSubmitRef = useRef(false);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const runVahanCheck = useCallback(
    async (allowMismatch = false) => {
      const reg = form.registrationNumber;
      if (!isValidIndianRegistration(reg)) {
        setVahanResult(null);
        setVahanState('idle');
        return;
      }
      if (!form.make?.trim() || !form.model?.trim() || !form.year) {
        return;
      }

      setVahanState('loading');
      try {
        const result = await yardVehicleService.vahanCheck({
          registrationNumber: reg,
          make: form.make,
          model: form.model,
          year: Number(form.year),
          allowMismatch,
        });
        setVahanResult(result);
        setVahanState('done');
        if (result.status === 'mismatch' && !allowMismatch) {
          setMismatchAttempts((n) => n + 1);
        }
        return result;
      } catch (err) {
        setVahanState('done');
        setVahanResult({
          status: 'api_down',
          message: err?.response?.data?.message || 'Vahan check failed',
        });
        return null;
      }
    },
    [form.registrationNumber, form.make, form.model, form.year]
  );

  useEffect(() => {
    if (step !== 3) return undefined;
    clearTimeout(vahanTimer.current);
    vahanTimer.current = setTimeout(() => {
      runVahanCheck(false);
    }, 600);
    return () => clearTimeout(vahanTimer.current);
  }, [step, form.registrationNumber, form.make, form.model, form.year, runVahanCheck]);

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = (file.type || '').toLowerCase();
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(type)) {
      toast.error('Only JPG or PNG images are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    setPhotoUploading(true);
    try {
      const url = await authService.uploadCompressedFile(file);
      update({ photoUrl: url });
      toast.success('Vehicle photo uploaded');
    } catch (err) {
      toast.error('Photo upload failed');
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };

  const toggleDamage = (value) => {
    setForm((prev) => {
      const set = new Set(prev.damageType);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...prev, damageType: [...set] };
    });
  };

  const canProceedStep1 = Boolean(form.photoUrl);
  const canProceedStep2 =
    form.make?.trim() &&
    form.model?.trim() &&
    form.year &&
    form.fuelType &&
    form.transmission &&
    form.damageType.length > 0 &&
    form.sourceType;

  const handleNext = () => {
    if (step === 1 && !canProceedStep1) {
      toast.error('Vehicle photo is required before continuing');
      return;
    }
    if (step === 2 && !canProceedStep2) {
      toast.error('Complete all vehicle detail fields');
      return;
    }
    setStep((s) => Math.min(3, s + 1));
  };

  const handleSubmit = async (forceMismatch = false) => {
    if (!form.photoUrl) {
      toast.error('Vehicle photo is required');
      setStep(1);
      return;
    }
    if (!canProceedStep2) {
      toast.error('Complete vehicle details');
      setStep(2);
      return;
    }
    if (!isValidIndianRegistration(form.registrationNumber)) {
      toast.error('Enter a valid registration (e.g. MH-12-AB-1234)');
      return;
    }

    setSubmitting(true);
    try {
      let check = vahanResult;
      if (!check || forceMismatch) {
        check = await runVahanCheck(forceMismatch);
      }

      if (check?.status === 'mismatch' && !forceMismatch) {
        toast.warning('Correct details to match RC, or submit again to flag for review');
        setSubmitting(false);
        return;
      }

      const payload = {
        ...form,
        year: Number(form.year),
        registrationNumber: formatIndianRegistration(form.registrationNumber),
        confirmMismatch: forceMismatch || check?.status === 'mismatch',
      };

      const result = await yardVehicleService.create(payload);
      const vehicle = result?.vehicle;

      if (vehicle?.vahanVerified) {
        toast.success('Vehicle added — RC verified. Generating parts catalog…');
      } else if (vehicle?.spotCheckRequired) {
        toast.info('Vehicle added — spot check required (Vahan unavailable)');
      } else if (vehicle?.fraudRisk) {
        toast.warning('Vehicle flagged for admin review due to RC mismatch');
      } else {
        toast.success('Vehicle added to yard inventory');
      }

      onSuccess?.(vehicle);
    } catch (err) {
      const code = err?.response?.data?.code;
      const data = err?.response?.data?.data;
      if (code === 'VAHAN_MISMATCH') {
        setVahanResult(data);
        setMismatchAttempts((n) => n + 1);
        toast.warning(data?.message || 'RC mismatch — please correct your entry');
      } else {
        toast.error(err?.response?.data?.message || 'Failed to add vehicle');
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (step !== 3 || submitting) return undefined;
    if (vahanState !== 'done' || !vahanResult) return undefined;
    if (vahanResult.status !== 'match') return undefined;
    if (autoSubmitRef.current) return undefined;
    autoSubmitRef.current = true;
    const t = setTimeout(() => handleSubmit(false), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vahanResult?.status, step, vahanState, submitting]);

  return (
    <div className="max-w-2xl">
      <StepIndicator step={step} />

      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-slate-900">Photograph the vehicle</h3>
          <p className="text-sm text-slate-600">
            A clear photo of the full vehicle is required. You cannot proceed without it.
          </p>
          <label
            className={`relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed min-h-[220px] cursor-pointer transition ${
              form.photoUrl
                ? 'border-emerald-300 bg-emerald-50/40'
                : 'border-slate-300 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/30'
            }`}
          >
            <input
              type="file"
              accept="image/jpeg,image/png"
              capture="environment"
              className="sr-only"
              onChange={handlePhotoSelect}
              disabled={photoUploading}
            />
            {form.photoUrl ? (
              <div className="relative w-full p-4">
                <img
                  src={form.photoUrl}
                  alt="Vehicle"
                  className="w-full max-h-64 object-cover rounded-xl"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    update({ photoUrl: '' });
                  }}
                  className="absolute top-6 right-6 bg-red-500 text-white rounded-full p-1.5 shadow"
                  aria-label="Remove photo"
                >
                  <FaTimes className="text-xs" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 py-10 px-6 text-center">
                {photoUploading ? (
                  <FaSpinner className="text-3xl text-emerald-600 animate-spin" />
                ) : (
                  <FaCamera className="text-4xl text-slate-400" />
                )}
                <span className="text-sm font-semibold text-slate-700">
                  Tap to photograph vehicle
                </span>
                <span className="text-xs text-slate-500">JPG or PNG · max 10MB</span>
              </div>
            )}
          </label>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-slate-900">Vehicle details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Make *</label>
              <input
                value={form.make}
                onChange={(e) => update({ make: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="e.g. Maruti"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Model *</label>
              <input
                value={form.model}
                onChange={(e) => update({ model: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="e.g. Swift"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Year *</label>
              <input
                type="number"
                min="1980"
                max="2100"
                value={form.year}
                onChange={(e) => update({ year: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="2019"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fuel type *</label>
              <select
                value={form.fuelType}
                onChange={(e) => update({ fuelType: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Select…</option>
                {FUEL_TYPE_OPTIONS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Transmission *</label>
              <select
                value={form.transmission}
                onChange={(e) => update({ transmission: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Select…</option>
                {TRANSMISSION_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source type *</label>
              <select
                value={form.sourceType}
                onChange={(e) => update({ sourceType: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Select…</option>
                {SOURCE_TYPE_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Damage type *</label>
            <div className="flex flex-wrap gap-2">
              {DAMAGE_TYPE_OPTIONS.map((d) => {
                const on = form.damageType.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDamage(d.value)}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium border transition ${
                      on
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-slate-900">Registration & Vahan RC check</h3>
          <p className="text-sm text-slate-600">
            Enter the registration number. We verify make, model, and year against the RC record.
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Registration number *
            </label>
            <input
              value={form.registrationNumber}
              onChange={(e) =>
                update({ registrationNumber: e.target.value.toUpperCase() })
              }
              onBlur={() => {
                if (form.registrationNumber) {
                  update({
                    registrationNumber: formatIndianRegistration(form.registrationNumber),
                  });
                }
              }}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono uppercase tracking-wide"
              placeholder="MH-12-AB-1234"
            />
            <VahanFeedback
              state={vahanState}
              result={vahanResult}
              mismatchAttempts={mismatchAttempts}
            />
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1">
            <p>
              <strong>Claimed:</strong> {form.make} {form.model} ({form.year}) · {form.fuelType} ·{' '}
              {form.transmission}
            </p>
            {form.photoUrl && (
              <p className="flex items-center gap-1 text-emerald-700">
                <FaCheckCircle /> Vehicle photo attached
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
        )}
        {step > 1 && (
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back
          </button>
        )}
        {step < 3 && (
          <button
            type="button"
            onClick={handleNext}
            disabled={step === 1 && !canProceedStep1}
            className="ml-auto px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        )}
        {step === 3 && (
          <>
            {vahanResult?.status === 'mismatch' && mismatchAttempts >= 1 && (
              <button
                type="button"
                onClick={() => handleSubmit(true)}
                disabled={submitting}
                className="px-4 py-2 rounded-lg border border-amber-400 text-amber-800 text-sm font-medium hover:bg-amber-50"
              >
                Submit for admin review
              </button>
            )}
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={submitting || vahanState === 'loading'}
              className="ml-auto px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? <FaSpinner className="animate-spin" /> : <FaTruck />}
              {vahanResult?.status === 'match' ? 'Creating vehicle…' : 'Add vehicle'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
